import { toast } from 'react-toastify';
import { tSuccess, tError } from '../utils/toast-helpers';
import { createSelector } from 'redux-bundler';
import { ContactsFormNames, FileTypes, LoadingMessages, ResponseStatuses, StorageScopes } from '@src/utils/enums';
import { filterNullEmptyObjects } from '../utils/helpers.jsx';
import { authStore, useApp, useModal } from '@stores';
import statusModal from '@src/app-pages/Forms/components/modals/statusModal.jsx';

const processJsonResponse = (response) =>
  new Promise((resolve, reject) => {
    const { openSecondaryModal } = useModal.getState();
    const { setIsLoading, setIsSavingProgress } = useApp.getState();
    const func = response.status < 400 ? resolve : reject;
    response
      .json()
      .then((json) =>
        func({
          status: response.status,
          json,
        })
      )
      .catch((e) => {
        console.error(e);
        // Stop any page or button loaders
        setIsLoading(false);
        setIsSavingProgress(false);
        // Display error modal
        openSecondaryModal(statusModal, {
          status: 'ERROR! Failed Uploads',
          msg: 'Retry saving/submitting your request in a few minutes.',
          saveText: 'Return to request',
        });
      });
  });

const processBlobResponse = (response) =>
  new Promise((resolve, reject) => {
    const func = response.status < 400 ? resolve : reject;
    response
      .blob()
      .then((blob) =>
        func({
          status: response.status,
          blob,
        })
      )
      .catch((e) => console.error(e));
  });

const handleFileDownload = (file, type, name) => {
  const link = document.createElement('a');
  const url = window.URL.createObjectURL(new Blob([file], { type: type }));
  link.href = url;
  link.download = name;
  link.click();
  window.URL.revokeObjectURL(url);
};

const fileStorageBundle = {
  name: 'fileStorage',

  getReducer: () => {
    const initialData = {
      fileUploaded: [],
      requestUploadedFiles: [],
      selectedFiles: [],
      componentFiles: [],
      // Size/count config
      totalFileSize: 0, // 0B
      maxTotalFileSize: 525000000, // 500MB + buffer
      maxIndFileSize: 106000000, // 100MB + buffer
      maxFileCount: 50,
    };

    return (state = initialData, { type, payload }) => {
      switch (type) {
        case 'UPDATE_UPLOAD_FILE':
          return { ...state, fileUploaded: payload };
        case 'UPDATE_REQUEST_UPLOADED_FILES':
          return { ...state, requestUploadedFiles: payload };
        case 'UPDATE_SELECTED_FILES':
          return { ...state, selectedFiles: [...state.selectedFiles, ...payload] };
        case 'UPDATE_COMPONENT_FILES':
          return { ...state, componentFiles: payload };
        case 'UPDATE_TOTAL_FILE_SIZE':
          return { ...state, totalFileSize: payload };
        case 'RESET_FILE_SECTION':
          return { ...state, selectedFiles: payload };
        case 'RESET_FILE_DATA':
          return initialData;
        default:
          return state;
      }
    };
  },

  selectFileStorage: (state) => state.fileStorage,
  selectRequestUploadedFiles: (state) => state.fileStorage.requestUploadedFiles,
  selectSelectedFiles: (state) => state.fileStorage.selectedFiles,
  selectComponentFiles: (state) => state.fileStorage.componentFiles,
  selectTotalFileSize: (state) => state.fileStorage.totalFileSize,
  selectMaxTotalFileSize: (state) => state.fileStorage.maxTotalFileSize,
  selectMaxIndFileSize: (state) => state.fileStorage.maxIndFileSize,
  selectMaxFileCount: (state) => state.fileStorage.maxFileCount,
  selectCombinedComponentFiles: createSelector(
    'selectComponentFiles',
    'selectSelectedFiles',
    (componentFiles, selectedFiles) => [...componentFiles, ...selectedFiles]
  ),
  selectCombinedRequestFiles: createSelector(
    'selectRequestUploadedFiles',
    'selectSelectedFiles',
    (requestUploadedFiles, selectedFiles) => [...requestUploadedFiles, ...selectedFiles]
  ),
  selectSupportingDocFiles: createSelector('selectCombinedComponentFiles', (files) =>
    files.filter((file) => file.section === 'Supporting Documents')
  ),
  selectAgentAuthorizationFile: createSelector('selectCombinedComponentFiles', (files) =>
    files.filter((file) => file.section === 'Agent Authorization')
  ),
  selectRightOfEntryFile: createSelector('selectCombinedComponentFiles', (files) =>
    files.filter((file) => file.section === 'Right of Entry')
  ),
  selectJpaFiles: createSelector('selectCombinedComponentFiles', (files) =>
    files.filter((file) => file.section === 'JPA')
  ),
  selectAirFiles: createSelector('selectCombinedComponentFiles', (files) =>
    files.filter((file) => file.section === 'Additional Information Request')
  ),
  selectGeneratedFiles: createSelector('selectRequestUploadedFiles', (files) => {
    const generatedFilesArr = files.filter((file) => file.DocumentType === 'Generated');
    var sortedArr = [];
    var existingArr = [];
    // Separate files that do/do not have the SortOrder S3 Tag
    generatedFilesArr.forEach((item) => (item?.SortOrder ? sortedArr.push(item) : existingArr.push(item)));
    // Sorted files then old files
    return [...sortedArr.sort((a, b) => Number(a.SortOrder) - Number(b.SortOrder)), ...existingArr];
  }),
  selectValidFiles: createSelector('selectSelectedFiles', (selectedFiles) =>
    selectedFiles?.filter((item) => item.isInvalid === false)
  ),

  doFetchUserFilesDetails:
    ({ componentID, getAllVersions = false, paramsID, scope = StorageScopes.ProjectFolder }) =>
    ({ dispatch, apiFetch, store }) => {
      const projectID = paramsID ? paramsID.projectID : store.selectProjectID();
      const requestID = paramsID ? paramsID.requestID : store.selectRequestID();
      const version = paramsID ? paramsID.version : store.selectVersion();

      const uri =
        '/api/FileStorage/getUserFilesDetails?' +
        new URLSearchParams({
          projectID,
          requestID,
          version,
          getAllVersions,
          scope,
        });

      return apiFetch(uri, { method: 'GET' })
        .then((response) => processJsonResponse(response))
        .then((response) => {
          // Format file payload
          var formatPayload = [];
          response?.json?.data?.map((file) => {
            const fileObj = {
              fileName: file.fileName,
              isUploadedToS3: file.isUploadedToS3,
              fileSize: file.fileSize,
              lastModified: file.lastModified,
            };
            var tagObj = {};
            file.tags.map((tag) => {
              const formatTag = { [tag.key]: tag.value };
              tagObj = { ...tagObj, ...formatTag };
              return true;
            });
            formatPayload.push({ ...fileObj, ...tagObj });
            return true;
          });
          dispatch({ type: 'UPDATE_REQUEST_UPLOADED_FILES', payload: formatPayload });
          store.doUpdateComponentFiles(componentID);
          return Promise.resolve(formatPayload);
        })
        .catch((e) => {
          dispatch({ type: 'FILES_FETCH_ERROR', payload: e });
          console.error(`Request returned: ${e}`);
          return Promise.resolve(false);
        });
    },
  doUploadFiles:
    (formData) =>
    ({ dispatch, apiFetch }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();

      const uri = '/api/FileStorage/uploadFiles';

      setIsLoading(true, LoadingMessages.UploadFile);

      return apiFetch(uri, { method: 'POST', body: formData })
        .then((response) => processJsonResponse(response))
        .then((response) => {
          if (response?.json?.status === ResponseStatuses.Success) {
            dispatch({ type: 'UPDATE_UPLOAD_FILES' });
          } else {
            dispatch({ type: 'UPLOAD_FILES_ERROR', payload: response?.json?.msg });
          }
          setIsLoading(false);
          return Promise.resolve({
            status: response?.json?.status,
            msg: response?.json?.msg,
          });
        })
        .catch((e) => {
          dispatch({ type: 'UPLOAD_FILE_ERROR' });
          console.error(`Request returned: ${e}`);
          // Stop any page or button loaders
          setIsLoading(false);
          setIsSavingProgress(false);
        });
    },
  doUploadFilesAnonymous:
    (formData) =>
    ({ dispatch, apiFetch }) => {
      const uri = '/api/FileStorage/uploadFilesAnonymous';
      const skipAuth = true;

      return apiFetch(uri, { method: 'POST', body: formData }, skipAuth)
        .then((response) => processJsonResponse(response))
        .then((response) => {
          if (response?.json?.status === ResponseStatuses.Success) {
            dispatch({ type: 'UPDATE_UPLOAD_FILES' });
          } else {
            dispatch({ type: 'UPLOAD_FILES_ERROR', payload: response?.json?.msg });
          }
          return Promise.resolve({
            status: response?.json?.status,
            msg: response?.json?.msg,
          });
        })
        .catch((e) => {
          dispatch({ type: 'UPLOAD_FILE_ERROR' });
          console.error(`Request returned: ${e}`);
        });
    },
  doUploadPublicNoticeAttachments:
    (formData) =>
    ({ dispatch, apiFetch }) => {
      const uri = '/api/FileStorage/uploadPublicNoticeAttachments';
      const skipAuth = true;

      return apiFetch(uri, { method: 'POST', body: formData }, skipAuth)
        .then((response) => processJsonResponse(response))
        .then((response) => {
          if (response?.json?.status === ResponseStatuses.Success) {
            dispatch({ type: 'UPDATE_UPLOAD_FILES' });
          } else {
            dispatch({ type: 'UPLOAD_FILES_ERROR', payload: response?.json?.msg });
          }
          return Promise.resolve({
            status: response?.json?.status,
            msg: response?.json?.msg,
          });
        })
        .catch((e) => {
          dispatch({ type: 'UPLOAD_FILE_ERROR' });
          console.error(`Request returned: ${e}`);
        });
    },
  doDownloadFile:
    (fileName, fileType, params = null, subFolder) =>
    async ({ apiFetch, store }) => {
      const toastId = toast.loading('Preparing file for download...');
      const projectID = params?.projectID ?? store.selectProjectID();
      const requestID = params?.requestID ?? store.selectRequestID();
      const version = params?.version ?? store.selectVersion();

      const skipAuth = fileType === 'Resource' && !authStore.isAuthenticated();

      const downloadUriMap = {
        [FileTypes.Template]: 'downloadTemplate',
        [FileTypes.Resource]: 'downloadResourceFile',
        [FileTypes.Request]: 'downloadFile',
      };

      const searchParams = { fileName };

      if (fileType === FileTypes.Request) {
        searchParams.projectID = projectID;
        searchParams.requestID = requestID;
        searchParams.version = version;
      }
      if (subFolder) {
        searchParams.subfolder = subFolder;
      }

      const urlSearchParams = new URLSearchParams(searchParams);
      const uri = `/api/FileStorage/${downloadUriMap[fileType]}?${urlSearchParams}`;
      const response = await apiFetch(uri, {}, skipAuth);
      const contentType = response.headers.get('content-type');
      const responseStatus = contentType.includes('application/json') ? (await response.clone().json()).status : null;

      if (['Error', 'Failed'].includes(responseStatus)) {
        tError(toastId, 'Failed to download file. Please try again later.');
      } else {
        const fileData = await processBlobResponse(response);
        const url = window.URL.createObjectURL(new Blob([fileData.blob]));
        const link = document.createElement('a');

        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);

        toast.dismiss(toastId);
      }
    },
  doDownloadPopulatedFile:
    (type, fileName) =>
    ({ store, apiFetch }) => {
      const toastId = toast.loading('Preparing file for download...');

      const uri = `/api/FileStorage/${type === ContactsFormNames.RightOfEntry ? 'generateRightOfEntry' : 'generateAgentAuth'}`;

      apiFetch(uri, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(store.selectRequestFormData()),
      })
        .then((response) => {
          // Clone response to read between json and blob types
          const responseClone = response.clone();
          // Fall back to blob if JSON parsing fails
          return response.json().catch(() => responseClone.blob());
        })
        .then((data) => {
          // Handle either the JSON data or the blob here
          if (data instanceof Blob) {
            // Process blob data
            handleFileDownload(data, 'application/pdf', fileName);
            toast.dismiss(toastId);
          } else {
            tError(toastId, data.msg);
          }
        })
        .catch((e) => {
          tError(toastId, 'Failed to download file. Please try again later.');
          console.error(`Request returned a ${e.status}`);
        });
    },
  doDownloadZip:
    (fileNames, paramsID) =>
    ({ apiFetch, store }) => {
      const toastId = paramsID?.toastID ?? toast.loading('Preparing file for download...');
      const projectID = paramsID?.projectID ?? store.selectProjectID();
      const requestID = paramsID?.requestID ?? store.selectRequestID();
      const version = paramsID?.version ?? store.selectVersion();

      const uri = '/api/FileStorage/downloadZippedFiles';

      const data = {
        FileNames: fileNames, // array of strings
        ProjectID: projectID,
        RequestID: requestID,
        Version: version,
      };

      apiFetch(uri, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
        .then((response) => {
          const contentDisposition = response.headers.get('Content-Disposition');
          let fileName = 'downloadedFile.zip'; // Default file name if none is provided

          if (contentDisposition) {
            const matches = /filename="(.+)"/.exec(contentDisposition);
            if (matches != null && matches[1]) {
              fileName = matches[1];
            }
          }

          // Convert the response into a Blob
          return response.blob().then((blob) => {
            const link = document.createElement('a');
            const url = window.URL.createObjectURL(new Blob([blob]));
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.dismiss(toastId);
          });
        })
        .catch((e) => {
          tError(toastId, 'Failed to download file. Please try again later.');
          console.error(`Request returned a ${e.status}`);
        });
    },
  doDeleteFile:
    (fileName, componentID, showToast = true) =>
    ({ dispatch, apiDelete, store }) => {
      const toastId = showToast === true ? toast.loading('Deleting file...') : null;
      const projectID = store.selectProjectID();
      const requestID = store.selectRequestID();
      const version = store.selectVersion();

      const uri =
        '/api/FileStorage/deleteDraftFile?' +
        new URLSearchParams({
          fileName,
          projectID,
          requestID,
          version,
        });

      apiDelete(uri, (err, body) => {
        if (!err && body.status === ResponseStatuses.Success) {
          dispatch({ type: 'DELETE_FILE_SUCCESSFUL', payload: fileName });
          showToast === true && tSuccess(toastId, 'File successfully deleted!');
          store.doFetchUserFilesDetails({ componentID: componentID, scope: StorageScopes.ProjectFolder });
        } else {
          showToast === true && tError(toastId, `File failed to delete! ${body.msg}`);
          dispatch({ type: 'DELETE_FILE_ERROR', payload: err });
        }
      });
    },
  doUpdateSelectedFiles:
    (data) =>
    ({ dispatch, store }) => {
      const excludeKeys = ['file', 'fileName', 'documents', 'isInvalid'];
      let tagList = [];
      Object.keys(data).map((key) => {
        if (!excludeKeys.includes(key)) {
          tagList.push({ [key]: data[key] });
        }
        return true;
      });
      const fileData = {
        file: data,
        tags: tagList,
      };
      dispatch({ type: 'UPDATE_SELECTED_FILES', payload: [{ ...fileData, ...data }] });
      dispatch({
        type: 'UPDATE_TOTAL_FILE_SIZE',
        payload:
          Number(isNaN(store.selectTotalFileSize()) ? 0 : store.selectTotalFileSize()) +
          Number(fileData?.file?.file?.size),
      });
    },
  doResetFileSection:
    (section, deleteUploaded = false) =>
    ({ dispatch, store }) => {
      const filteredFiles = store.selectSelectedFiles().filter((el) => el.section !== section);
      const filteredUploadedFiles = store.selectRequestUploadedFiles().filter((file) => file.section === section);
      const sum = filteredUploadedFiles.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      dispatch({ type: 'RESET_FILE_SECTION', payload: filteredFiles });
      dispatch({
        type: 'UPDATE_TOTAL_FILE_SIZE',
        payload: Number(isNaN(store.selectTotalFileSize()) ? 0 : Number(sum)),
      });
      // @TODO: Move bulk delete file logic to backend
      if (deleteUploaded) {
        filteredUploadedFiles?.length > 0 &&
          filteredUploadedFiles.forEach((item) => {
            store.doDeleteFile(item.fileName, item.componentID, false);
          });
      }
    },
  doResetJPAFiles:
    (jpaQuestionID, deleteUploaded = false) =>
    ({ dispatch, store }) => {
      const filteredFiles = store.selectSelectedFiles().filter((el) => el.jpaQuestionID !== jpaQuestionID);
      const filteredUploadedFiles = store
        .selectRequestUploadedFiles()
        .filter((file) => file.jpaQuestionID === jpaQuestionID);
      const sum = filteredUploadedFiles.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      dispatch({ type: 'RESET_FILE_SECTION', payload: filteredFiles });
      dispatch({
        type: 'UPDATE_TOTAL_FILE_SIZE',
        payload: Number(isNaN(store.selectTotalFileSize()) ? 0 : Number(sum)),
      });
      // @TODO: Move bulk delete file logic to backend
      if (deleteUploaded) {
        filteredUploadedFiles?.length > 0 &&
          filteredUploadedFiles.forEach((item) => {
            store.doDeleteFile(item.fileName, item.componentID, false);
          });
      }
    },
  // For the dynamic forms
  doResetFilesByQuestionId:
    (questionId, deleteUploaded = false) =>
    ({ dispatch, store }) => {
      const filteredFiles = store.selectSelectedFiles().filter((el) => el.questionID !== questionId);
      const filteredUploadedFiles = store.selectRequestUploadedFiles().filter((file) => file.questionID === questionId);
      const sum = filteredUploadedFiles.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      dispatch({ type: 'RESET_FILE_SECTION', payload: filteredFiles });
      dispatch({
        type: 'UPDATE_TOTAL_FILE_SIZE',
        payload: Number(isNaN(store.selectTotalFileSize()) ? 0 : Number(sum)),
      });
      // @TODO: Move bulk delete file logic to backend
      if (deleteUploaded) {
        filteredUploadedFiles?.length > 0 &&
          filteredUploadedFiles.forEach((item) => {
            store.doDeleteFile(item.fileName, item.componentID, false);
          });
      }
    },
  doResetUsersFileData:
    () =>
    ({ dispatch }) => {
      dispatch({ type: 'RESET_FILE_DATA' });
    },
  doUpdateComponentFiles:
    (componentID) =>
    ({ dispatch, store }) => {
      const filteredArr = store.selectRequestUploadedFiles().filter((item) => item.componentID === String(componentID));
      dispatch({ type: 'UPDATE_COMPONENT_FILES', payload: filteredArr });
    },
  doGenerateDraftDocuments:
    () =>
    ({ store, apiFetch }) => {
      const toastId = toast.loading('Downloading Draft Documents...');
      const filteredData = filterNullEmptyObjects(store.selectRequestFormData());

      const uri = '/api/FileStorage/generateDraftDocuments';

      apiFetch(uri, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filteredData),
      })
        .then((response) => {
          // Clone response to read between json and blob types
          const responseClone = response.clone();
          // Fall back to blob if JSON parsing fails
          return response.json().catch(() => responseClone.blob());
        })
        .then((data) => {
          // Handle either the JSON data or the blob here
          if (data instanceof Blob) {
            // Process blob data
            const fileName = `${filteredData?.projectID ? filteredData.projectID + '_' : '0'}${filteredData?.requestID ? filteredData.requestID + '_' : '0'}${filteredData?.version ? filteredData.version : '0'}_Draft_Files`;
            handleFileDownload(data, 'application/zip', fileName);
            toast.dismiss(toastId);
          } else {
            // Process JSON data
            tError(toastId, data.msg);
          }
        })
        .catch((e) => {
          tError(toastId, 'Failed to download file. Please try again later.');
          console.error(`Request returned a ${e.status}`);
        });
    },
};

export default fileStorageBundle;
